import { startCase } from 'lodash';
import { compact, flatten, uniqBy } from 'lodash/fp';

import { getSchemaForFieldPath } from 'dashboard/components/JsonSchemaSelect/utils'; // TODO: (kengo) stop importing from subdirectory: A cypress spec script (workflows-show-2.0) crashes by missing loader for .svg if import top-level JsonSchemaSelect which involves a SVG file. We should find a configuration to avoid importing SVG or another way to avoid importing .svg
import {
  getJsonSchemaType,
  JsonLogicValue,
  RuleValueTypes,
  snakify,
  UsableObjectConfig,
  valueTransformer,
} from 'dashboard/lib/workflows';
import fieldMetas from 'dashboard/lib/workflows/fieldMetas';
import {
  JsonLogicField,
  RuleValueConfigField,
  RuleValueConfigFieldMulti,
  RuleValueConfigValue,
} from 'dashboard/lib/workflows/json-logic';
import { WorkflowFieldKey, WorkflowOperatorKey } from 'dashboard/models/workflow';
import {
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { JsonSchemaAllTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaArray, JsonSchemaString } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';
import { TokensToNode } from 'lib/workflows';

export enum BinaryOperator {
  And = 'and',
  Or = 'or',
}

export interface LogicBlockOrV2 {
  or: LogicBlockV2[];
}

export const EMPTY_LOGIC_BLOCK_OR_V2: LogicBlockOrV2 = { or: [{ and: [{}] }] };

export interface LogicBlockV2 {
  and: ConditionalRule[];
}

export type ConditionalRule = Partial<Record<WorkflowOperatorKey, ConditionalRuleRightHand>>;
export type ConditionalRuleRightHand = [JsonLogicField?, ConditionalRuleValueAll?];
export type ConditionalRuleValueAll = JsonLogicValue | JsonLogicField | JsonLogicField[];

export function getOperatorDisplayName(operator: WorkflowOperatorKey): string {
  switch (operator) {
    case WorkflowOperatorKey.Equals:
    case WorkflowOperatorKey.DoubleEquals:
      return 'equals';
    case WorkflowOperatorKey.DoesNotEqual:
      return 'does not equal';
    case WorkflowOperatorKey.Concat:
      return 'add';
    case WorkflowOperatorKey.Contains:
      return 'includes any item of';
    case WorkflowOperatorKey.ContainsAnySubstring:
      return 'contains text';
    case WorkflowOperatorKey.ContainsNoSubstring:
      return 'does not contain text';
    case WorkflowOperatorKey.StartsWith:
      return 'starts with any of';
    case WorkflowOperatorKey.LessThan:
      return '<';
    case WorkflowOperatorKey.LessThanOrEqual:
      return '<=';
    case WorkflowOperatorKey.GreaterThan:
      return '>';
    case WorkflowOperatorKey.GreaterThanOrEqual:
      return '>=';
    case WorkflowOperatorKey.In:
      return 'is in';
    case WorkflowOperatorKey.InList:
      return 'is in List';
    case WorkflowOperatorKey.NotIn:
      return 'is not in';
    case WorkflowOperatorKey.NotInList:
      return 'is not in List';
    case WorkflowOperatorKey.IncludesAll:
      return 'includes every item of';
    case WorkflowOperatorKey.IncludesNotAll:
      return 'does not include every item of';
    case WorkflowOperatorKey.IncludesOnly:
      return 'includes only';
    case WorkflowOperatorKey.IncludesNone:
      return 'does not include any item of';
    case WorkflowOperatorKey.IncludesExactly:
      return 'equals';
    case WorkflowOperatorKey.IncludesNotExactly:
      return 'does not equal';
    case WorkflowOperatorKey.IsBlank:
      return 'is blank';
    case WorkflowOperatorKey.IsNotBlank:
      return 'is not blank';
    case WorkflowOperatorKey.IsFalsy:
      return 'is falsy';
    case WorkflowOperatorKey.IsTruthy:
      return 'is truthy';
    case WorkflowOperatorKey.AccountNameSubstringMatchAll:
      return 'matches all';
    case WorkflowOperatorKey.AccountNameSubstringMatchNotAll:
      return 'does not match all';
    case WorkflowOperatorKey.AccountNameSubstringMatchAny:
      return 'matches any';
    case WorkflowOperatorKey.AccountNameSubstringMatchNone:
      return 'matches none';
    case WorkflowOperatorKey.MatchesPercentage:
      return 'of';
    case WorkflowOperatorKey.MatchesRegex:
      return 'matches regex';
    case WorkflowOperatorKey.MatchesSubstring:
      return 'matches substring';
    case WorkflowOperatorKey.MismatchesSubstring:
      return 'does not match substring';
    case WorkflowOperatorKey.Add:
      return 'add';
    case WorkflowOperatorKey.Subtract:
      return 'subtract';
    case WorkflowOperatorKey.Multiply:
      return 'multiply';
    case WorkflowOperatorKey.Divide:
      return 'divide';
    case WorkflowOperatorKey.OrAssign:
      return 'fallback if invalid';
    case WorkflowOperatorKey.And:
    case WorkflowOperatorKey.Or:
    case WorkflowOperatorKey.Sum:
    case WorkflowOperatorKey.Avg:
    case WorkflowOperatorKey.Reduce:
      return operator;
    case WorkflowOperatorKey.AgeGreaterThan:
      return '> age';
    case WorkflowOperatorKey.AgeGreaterThanOrEqual:
      return '>= age';
    case WorkflowOperatorKey.AgeLessThan:
      return '< age';
    case WorkflowOperatorKey.AgeLessThanOrEqual:
      return '<= age';
    case WorkflowOperatorKey.SizeGreaterThan:
      return 'size >';
    case WorkflowOperatorKey.SizeGreaterThanOrEqual:
      return 'size >=';
    case WorkflowOperatorKey.SizeLessThan:
      return 'size <';
    case WorkflowOperatorKey.SizeLessThanOrEqual:
      return 'size <=';
    case WorkflowOperatorKey.SizeEquals:
      return 'size =';
    case WorkflowOperatorKey.VersionGreaterThan:
      return '> version';
    case WorkflowOperatorKey.VersionGreaterThanOrEqual:
      return '>= version';
    case WorkflowOperatorKey.VersionLessThan:
      return '< version';
    case WorkflowOperatorKey.VersionLessThanOrEqual:
      return '<= version';
    default:
      return assertNever(operator);
  }
}

export const parseFieldPath = (
  value: string,
  usableObjects: UsableObjectConfig[],
): {
  previousPath: string;
  criteriaVariableKey: WorkflowFieldKey;
} => {
  if (!value) {
    return {
      previousPath: '',
      criteriaVariableKey: '' as WorkflowFieldKey,
    };
  }

  const [firstPart, ...remainingPath] = value.replace(/_/g, '-').split('.');
  let previousPath = firstPart;
  const criteriaVariableKey = remainingPath.find((pathPart) => {
    const objectMatch = usableObjects.find(({ value }) => value === previousPath);
    if (objectMatch && fieldMetas[pathPart as WorkflowFieldKey]) {
      return true;
    }
    previousPath += `.${pathPart}`;
    return false;
  }) as WorkflowFieldKey;
  return { previousPath, criteriaVariableKey };
};

export const getConditionalPathDisplayText = (
  path: string,
  usableObjects: UsableObjectConfig[],
  jsonSchema: JsonSchema,
  tokensToSteps: TokensToNode,
  passedTokensToDisplayValue?: Record<string, string>,
) => {
  const splitPath = path.split('.');
  const tokensToDisplayValue = passedTokensToDisplayValue || {};
  const step: WorkflowStep = tokensToSteps[splitPath[0]]?.step as WorkflowStep;

  const labelOfResultOrErrorFor = (label: string, splitPath: string[]) => {
    const finalLink = splitPath.length > 0 ? splitPath[splitPath.length - 1] : '';
    const endString =
      (splitPath.length > 0 && finalLink === 'error_message') || finalLink === 'error_type'
        ? finalLink
        : 'Result';
    return `${label} ${startCase(endString)}`;
  };

  if (step?.type === WorkflowStepType.Loop) {
    return 'Loop Step Result';
  } else if (step?.type === WorkflowStepType.Action) {
    const actionStep = step as WorkflowStepAction;

    switch (actionStep?.action?.key) {
      case WorkflowStepActionKey.Calculate:
        return labelOfResultOrErrorFor('Calculate', splitPath);
      case WorkflowStepActionKey.EvaluateCode:
        return labelOfResultOrErrorFor('Evaluate Code', splitPath);
      case WorkflowStepActionKey.HttpRequest:
        return labelOfResultOrErrorFor('HTTP Request', splitPath);
      case WorkflowStepActionKey.ListMatch:
        return labelOfResultOrErrorFor('List Match', splitPath);
      case WorkflowStepActionKey.RunHttpPlatformIntegration:
      case WorkflowStepActionKey.RunGraphQuery:
        return valueTransformer(tokensToDisplayValue).getDisplayValue(path);
    }
  }

  const { previousPath, criteriaVariableKey } = parseFieldPath(path, usableObjects);
  const previousPathSnake = valueTransformer(tokensToDisplayValue).getDisplayValue(
    snakify(previousPath),
  );
  const criteriaVariableKeySnake = snakify(criteriaVariableKey);
  const { schema: criteriaVariableJsonSchema } = getSchemaForFieldPath({
    path: `${previousPathSnake}.${criteriaVariableKeySnake}`,
    initialSchema: jsonSchema,
  });
  const knownName = fieldMetas[criteriaVariableKey]?.name;

  if (!knownName || !criteriaVariableJsonSchema) {
    return compact([startCase(previousPathSnake), startCase(criteriaVariableKeySnake)]).join(' - ');
  }

  const schemaType = getJsonSchemaType(criteriaVariableJsonSchema);
  if (schemaType === JsonSchemaAllTypes.Object) {
    let nestedCriteriaVariable = path.split(`.${criteriaVariableKeySnake}.`).pop();
    nestedCriteriaVariable = valueTransformer(tokensToDisplayValue).getDisplayValue(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nestedCriteriaVariable,
    );
    const prettyPath = compact([startCase(previousPathSnake), knownName]).join(' ');
    return compact([prettyPath, startCase(nestedCriteriaVariable)]).join(' - ');
  }

  return compact([startCase(previousPathSnake), knownName]).join(' - ');
};

export type ConditionalRuleValueTypes = RuleValueTypes.Field | RuleValueTypes.Value;
export type ConditionalRuleValueConfig =
  | RuleValueConfigField
  | RuleValueConfigFieldMulti
  | RuleValueConfigValue;

export const getConditionalRuleValueConfig = (
  ruleValue: ConditionalRuleValueAll,
): ConditionalRuleValueConfig => {
  if (ruleValue == null) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  }

  if (
    Array.isArray(ruleValue) &&
    (ruleValue as JsonLogicField[]).every(
      (val: JsonLogicField) => typeof val === 'object' && 'var' in val,
    )
  ) {
    const paths = (ruleValue as JsonLogicField[]).map((val) => val.var);
    return {
      type: RuleValueTypes.Field,
      value: ruleValue as JsonLogicField[],
      paths,
    };
  }

  if (typeof ruleValue === 'object' && 'var' in ruleValue) {
    return {
      type: RuleValueTypes.Field,
      value: ruleValue,
      paths: [ruleValue.var],
    };
  }

  return {
    type: RuleValueTypes.Value,
    value: ruleValue as JsonLogicValue,
    paths: [],
  };
};

export type ConditionalRuleParts = {
  operator: WorkflowOperatorKey;
  valueLeftConfig: RuleValueConfigField;
  valueRightConfig?: ConditionalRuleValueConfig;
};

export const getConditionalRuleParts = (rule: ConditionalRule): ConditionalRuleParts => {
  const operator = Object.keys(rule)[0] as WorkflowOperatorKey;
  const values = rule[operator];

  if (!operator || !values?.length) {
    return {
      operator: operator || null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      valueLeftConfig: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      valueRightConfig: null,
    };
  }

  return {
    operator,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    valueLeftConfig: getConditionalRuleValueConfig(values[0]) as RuleValueConfigField,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    valueRightConfig: getConditionalRuleValueConfig(values[1]),
  };
};

export const isEmptyRule = (
  valueLeftConfig: ConditionalRuleParts['valueLeftConfig'],
  valueRightConfig: ConditionalRuleParts['valueRightConfig'],
): boolean => {
  return !valueLeftConfig && !valueRightConfig;
};

export const generateConditionalOptions = (
  jsonSchema: JsonSchema,
): { label: string; value: string }[] => {
  const schemaType = getJsonSchemaType(jsonSchema);

  switch (schemaType) {
    case JsonSchemaAllTypes.Enum: {
      return generateEnumOptions(jsonSchema as JsonSchemaString);
    }
    case JsonSchemaAllTypes.MultiEnum: {
      return generateEnumOptions((jsonSchema as JsonSchemaArray<JsonSchemaString>).items);
    }
    case JsonSchemaAllTypes.GroupedEnum: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return uniqBy('value', flatten(jsonSchema.anyOf.map(generateEnumOptions)));
    }
    default:
      return [];
  }
};

export const generateEnumOptions = (jsonSchema: JsonSchemaString) => {
  if (jsonSchema.oneOf) {
    return jsonSchema.oneOf.map((val) => ({ label: val.title || val.const, value: val.const }));
  } else {
    return (jsonSchema.enum || []).map((val, i) => ({
      label: jsonSchema.labels?.[i] ?? val,
      value: val,
    }));
  }
};

export const generateListOptions = (lists: { name: string; id: string; type: string }[]) => {
  return lists.map((list) => {
    return {
      label: list.name,
      value: list.id,
    };
  });
};

/**
 * Get display friendly rule value
 * For enum types, uses display labels (if available) instead of actual values
 * e.g. For tags, uses tag name instead of token
 */
export const getRuleDisplayValue = (
  value: JsonLogicValue,
  fullPath: string,
  jsonSchema: JsonSchema,
) => {
  if (value == null) {
    return null;
  }

  const { schema: fieldJsonSchema } = getSchemaForFieldPath({
    path: fullPath,
    initialSchema: jsonSchema,
  });
  if (!fieldJsonSchema) {
    return value;
  }

  const options = generateConditionalOptions(fieldJsonSchema);

  if (!options.length) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((v) => {
      return options.find((option) => option.value === v)?.label || v;
    });
  }

  return options.find((option) => option.value === value)?.label || value;
};

export const getRuleSideDisplay = ({
  jsonSchema,
  otherConfig,
  ruleValueConfig,
  tokensToDisplayValue,
  tokensToSteps,
  usableObjects,
}: {
  jsonSchema: JsonSchema;
  otherConfig?: ConditionalRuleValueConfig;
  ruleValueConfig: ConditionalRuleValueConfig;
  tokensToDisplayValue: Record<string, string>;
  tokensToSteps: TokensToNode;
  usableObjects: UsableObjectConfig[];
}): JsonLogicValue | null => {
  const otherPath = otherConfig?.type === RuleValueTypes.Field ? otherConfig.paths[0] : '';
  const { type, value, paths } = ruleValueConfig;
  switch (type) {
    case RuleValueTypes.Value: {
      return getRuleDisplayValue(
        value as JsonLogicValue,
        valueTransformer(tokensToDisplayValue).getDisplayValue(otherPath),
        jsonSchema,
      );
    }
    case RuleValueTypes.Field: {
      return paths.map((path: string) =>
        getConditionalPathDisplayText(
          path,
          usableObjects,
          jsonSchema,
          tokensToSteps,
          tokensToDisplayValue,
        ),
      );
    }
    default: {
      return assertNever(type);
    }
  }
};
